import cx from "classnames";
import PropTypes from "prop-types";
import React, { forwardRef, useCallback, useState } from "react";
import Button from "react-bootstrap/Button";
import { omit } from "../../utils/utils";
import { ColorOptions, IFXIcons } from "./IFXIcons";
import { withTooltip } from "./IFXTooltip";

export const ButtonMode = {
  ICON_LABEL: 1,
  LABEL: 2,
  ICON: 3,
  LABEL_ICON: 4,
  ICON_MINI: 5,
  LABEL_MINI: 6,
  LABEL_FULL_MINI: 7,
};

export const ButtonConfig = {
  default: {
    className: "ifx-button",
    backgroundStyle: false,
    hoverStyle: "btn-transition",
    outline: true,
    color: "primary",
    name: "Button",
    mode: ButtonMode.LABEL,
    htmlType: "button",
    size: "sm",
    wider: false,
    shape: null,
    shadow: false,
  },
  search: {
    color: "primary",
    name: "Search",
    icon: "search",
    mode: ButtonMode.LABEL,
    htmlType: "submit",
  },
  update: {
    color: "primary",
    name: "Update",
    mode: ButtonMode.LABEL,
    htmlType: "submit",
  },
  submit: {
    color: "primary",
    name: "Submit",
    htmlType: "submit",
  },
  save: {
    color: "primary",
    name: "Save",
    htmlType: "submit",
    icon: ["far", "save"],
    mode: ButtonMode.LABEL,
  },
  saveall: {
    color: "primary",
    name: "Save All",
    htmlType: "submit",
  },
  select: {
    name: "Select",
  },
  close: {
    color: "alternate",
    name: "Close",
    mode: ButtonMode.LABEL,
  },
  edit: {
    color: "primary",
    name: "Edit",
    icon: "edit",
    mode: ButtonMode.LABEL,
  },
  add: {
    color: "primary",
    name: "Add",
    icon: "plus",
  },
  clear: {
    color: "secondary",
    name: "Clear",
    htmlType: "reset",
    icon: "eraser",
  },
  next: {
    color: "secondary",
    name: "Next",
    htmlType: "next",
    icon: "next",
  },
  refresh: {
    color: "alternate",
    name: "Refresh",
    htmlType: "button",
    icon: "sync-alt",
    mode: ButtonMode.LABEL,
  },
  reload: {
    color: "alternate",
    name: "Reload",
    htmlType: "button",
    icon: "sync-alt",
    mode: ButtonMode.LABEL,
  },
  generate: {
    color: "alternate",
    name: "Generate",
    htmlType: "button",
  },
  delete: {
    color: "danger",
    name: "Delete",
    icon: "times",
    mode: ButtonMode.LABEL,
  },
  remove: {
    color: "danger",
    name: "Remove",
    icon: ["far", "trash-alt"],
  },
  upload: {
    color: "primary",
    name: "Upload",
    htmlType: "submit",
  },
  ok: {
    color: "primary",
    name: "Ok",
  },
  yes: {
    color: "primary",
    name: "Yes",
  },
  no: {
    color: "secondary",
    name: "no",
  },
  cancel: {
    color: "secondary",
    name: "Cancel",
    icon: "ban",
  },
  download: {
    color: "alternate",
    name: "Download",
    icon: "download",
    mode: ButtonMode.LABEL,
  },
  export: {
    color: "alternate",
    name: "Export",
    icon: "file-download",
    mode: ButtonMode.LABEL,
  },
  print: {
    color: "alternate",
    name: "Print",
    icon: "print",
    mode: ButtonMode.LABEL,
  },
  reset: {
    color: "alternate",
    name: "Reset",
    mode: ButtonMode.LABEL,
  },
  clearfilter: {
    color: "alternate",
    name: "Clear Filter",
    mode: ButtonMode.LABEL,
  },
  copy: {
    color: "alternate",
    name: "Copy",
    icon: ["far", "clone"],
    mode: ButtonMode.LABEL,
  },
  lookup: {
    color: "dark",
    name: "Lookup",
    icon: "binoculars",
    mode: ButtonMode.LABEL,
  },
  zoomin: {
    color: "primary",
    name: "Zoom In",
    icon: "search-plus",
    mode: ButtonMode.LABEL,
  },
  zoomout: {
    color: "primary",
    name: "Zoom Out",
    icon: "search-minus",
    mode: ButtonMode.LABEL,
  },
  image: {
    color: "primary",
    name: "Image",
    icon: "image",
    mode: ButtonMode.LABEL,
  },
  lockuser: {
    color: "danger",
    name: "Lock User",
    icon: "user-lock",
    mode: ButtonMode.LABEL,
  },
  link: {
    color: "link",
    name: "link",
    mode: ButtonMode.LABEL,
    hoverStyle: "",
    outline: false,
  },
  primary: {
    color: "primary",
    name: "primary",
    mode: ButtonMode.LABEL,
  },
  secondary: {
    color: "secondary",
    name: "secondary",
    mode: ButtonMode.LABEL,
  },
  alternate: {
    color: "alternate",
    name: "alternate",
    mode: ButtonMode.LABEL,
  },
  success: {
    color: "success",
    name: "success",
    mode: ButtonMode.LABEL,
  },
  info: {
    color: "info",
    name: "info",
    mode: ButtonMode.LABEL,
  },
  warning: {
    color: "warning",
    name: "warning",
    mode: ButtonMode.LABEL,
  },
  danger: {
    color: "danger",
    name: "danger",
    mode: ButtonMode.LABEL,
  },
  dark: {
    color: "dark",
    name: "dark",
    mode: ButtonMode.LABEL,
  },
  "ifx-brand": {
    color: "ifx-brand",
    name: "ifx-brand",
    mode: ButtonMode.LABEL,
  },
  "ifx-light": {
    color: "ifx-light",
    name: "ifx-light",
    mode: ButtonMode.LABEL,
  },
  "ifx-alternate": {
    color: "ifx-alternate",
    name: "ifx-alternate",
    mode: ButtonMode.LABEL,
  },
  "approve-reject": {
    name: ["Approve", "Reject"],
    icon: ["approve", "reject"],
    iconProps: { iconSource: "svg" },
    isToggle: true,
  },
  next: {
    name: "Next",
    htmlType: "submit",
  },
};

const IFXButtonFinal = forwardRef((props, ref) => {
  //  console.log("IFXButtonFinal", props);

  let {
    tooltip = false,
    htmlType,
    mode,
    icon,
    wider,
    hoverStyle,
    backgroundStyle = false,
    name,
    shape,
    variant,
    outline,
    color,
    shadow,
    iconProps = {},
    tooltipProps = {},
    marginClassName = "mr-1",
    bubbleTip,
    size,
  } = props;

  // console.log("IFXButtonFinal props", props);

  variant = variant ? variant : outline ? "outline" : false;
  variant = variant
    ? variant + (color ? "-" + color : "")
    : color
    ? color
    : undefined;

  let newProps = { "aria-label": name, ...props, variant };

  newProps = omit(newProps, [
    "tooltip",
    "htmlType",
    "mode",
    "icon",
    "wider",
    "hoverStyle",
    "backgroundStyle",
    "shape",
    "color",
    "outline",
    "iconProps",
    "buttonConfig",
    "shadow",
    "isToggle",
    "tooltipProps",
    "marginClassName",
    "bubbleTip",
    "iconSource",
  ]);

  if (mode === ButtonMode.LABEL) newProps = omit(newProps, ["title"]);

  let children = <></>;
  if (!newProps.children || newProps.children == null) {
    switch (mode) {
      case ButtonMode.LABEL_FULL_MINI:
      case ButtonMode.LABEL: {
        children = <div className="text-nowrap">{newProps.name}</div>;
        break;
      }
      case ButtonMode.LABEL_MINI: {
        let [labelChar] = newProps.name;
        children = <div className="text-nowrap">{labelChar}</div>;
        break;
      }
      case ButtonMode.LABEL_ICON: {
        children = (
          <div className="text-nowrap">
            {newProps.name}
            <IFXIcons className="ml-1" icon={icon} {...iconProps} />
          </div>
        );
        break;
      }
      case ButtonMode.ICON_MINI:
      case ButtonMode.ICON: {
        children = <IFXIcons icon={icon} {...iconProps} />;
        break;
      }
      case ButtonMode.ICON_LABEL: {
        children = (
          <div className="text-nowrap">
            <IFXIcons className="mr-1" icon={icon} {...iconProps} />
            {newProps.name}
          </div>
        );

        break;
      }
      default: {
      }
    }
  }

  const baseButton = (
    <Button
      ref={ref}
      {...newProps}
      className={cx(
        ButtonConfig.default.className,
        (mode !== ButtonMode.ICON_MINI &&
          mode !== ButtonMode.LABEL_MINI &&
          mode !== ButtonMode.LABEL_FULL_MINI &&
          marginClassName) ||
          "",
        {
          "btn-wide": wider,
          "btn-shadow": shadow,
          "button-mini":
            mode === ButtonMode.ICON_MINI || mode === ButtonMode.LABEL_MINI,
          "label-mini": mode === ButtonMode.LABEL_MINI,
          "label-full-mini": mode === ButtonMode.LABEL_FULL_MINI,
          "icon-mini": mode === ButtonMode.ICON_MINI,
          "shape-circle": shape === "circle",
        },
        hoverStyle,
        backgroundStyle,
        props.className
      )}
      type={htmlType}
    >
      {newProps.children ? newProps.children : <>{children}</>}
    </Button>
  );

  const buttonWithTooltip =
    (tooltip &&
      withTooltip(baseButton, {
        placement: "top",
        content: props.name,
        ...tooltipProps,
      })) ||
    baseButton;

  const finalButton = !!bubbleTip
    ? withBubbleTip(buttonWithTooltip, { bubbleTip, size, color })
    : buttonWithTooltip;

  return finalButton;
});

export const withBubbleTip = (child, props) =>
  React.createElement(ButtonWithBubble, {
    children: child,
    ...props,
  });

export const ButtonWithBubble = ({
  children,
  bubbleTip,
  color,
  size = "lg",
}) => {
  return (
    <div className="ifx-bubble-btn-wrapper">
      {children}
      <div
        className={`ifx-bubble-btn-bubble bubble-color-${color} bubble-${size}`}
      >
        <span>{bubbleTip}</span>
      </div>
    </div>
  );
};

export const IFXButton = forwardRef((props, ref) => {
  const { type = "default" } = props;
  const selectedConfig = ButtonConfig[type.toLowerCase()];
  const newProps = {
    ...ButtonConfig.default,
    ...selectedConfig,
    ...props,
  };

  const { isToggle, defaultToggle, onToggle, ..._newProps } = newProps;
  const button = <IFXButtonFinal ref={ref} {..._newProps} />;

  //console.log("isToggle", isToggle);
  return (isToggle && asToggle(button, { defaultToggle, onToggle })) || button;
});

export const ButtonIconConfig = {
  default: {
    mode: ButtonMode.ICON_MINI,
    iconProps: { iconSource: "svg" },
    backgroundStyle: "btn-transparent",
    outline: false,
    variant: "ifx-monochrome",
    color: ColorOptions.ifxBrand,
    icon: "question_default",
    //name: "My Icon Button",
  },
  save: {
    icon: "floppy-disk",
  },
  add: {
    icon: "add",
  },
  clear: {
    icon: "clear",
  },
  reload: {
    icon: "refresh",
  },
  "export-csv": {
    name: "Export CSV",
    icon: "csv",
  },
  delete: {
    icon: "delete",
  },
  print: {
    name: "Print",
    icon: "printer",
  },
  edit: {
    name: "Edit",
    icon: "edit",
  },
  options: {
    name: "Options",
    icon: "gear",
  },
  lookup: {
    icon: "search",
  },
  info: {
    icon: "info",
  },
  approve: {
    icon: "approve",
    name: "Approve",
  },
  propose: {
    icon: "propose",
    name: "Propose",
  },
  price: {
    icon: "price",
    name: "Price",
  },
  whereused: {
    icon: "whereused",
    name: "Where Used",
  },
  timeline: {
    icon: "timeline",
    name: "Version Timeline",
  },
  change: {
    icon: "change",
    name: "Change",
  },
  history: {
    icon: "history",
    name: "History",
  },
  obsolete: {
    icon: "obsolete",
    name: "Obsolete",
  },
  cancel: {
    icon: "cancel",
  },
  close: {
    icon: "cancel",
  },
  copy: {
    icon: "copy",
    name: "Copy",
  },
  spec: {
    icon: "spec",
    name: "Spec",
  },
  back: {
    name: "Back",
    icon: "back",
  },
  next: {
    name: "Next",
    icon: "next",
  },
  history: {
    icon: "history",
  },
  visibility: {
    icon: "visibility",
  },
  clearfilter: {
    icon: "unfilter",
  },
  reset: {
    icon: "reset",
  },
  report: {
    icon: "report",
  },
  "approve-reject": {
    name: ["Approve", "Reject"],
    icon: ["approve", "reject"],
    isToggle: true,
  },
  next: {
    icon: "next",
  },
  res_code: {
    icon: "res_code",
    name: " Res Codes",
  },
  legend: {
    icon: "legend",
    name: "Service Code Legend",
  },
};

export const IFXToggleButton = ({ children, ...toggleProps }) => {
  // console.log("IFXToggleButton children", children);
  //  console.log("IFXToggleButton children props", children.props);

  const {
    onToggle = toggle => {
      console.log("onToggle", toggle);
    },
    defaultToggle = false,
  } = toggleProps;
  const {
    icon: [onIcon, OffIcon],
    name: [onName, offName] = ["on", "off"],
    ...props
  } = children.props;

  const [toggle, setToggle] = useState(defaultToggle);

  const _toggleProps = (toggle && { icon: onIcon, name: onName }) || {
    icon: OffIcon,
    name: offName,
  };

  const onClick = useCallback(() => {
    const _toggle = !toggle;
    //return true in onToggle to prevent toggle state.
    !onToggle(_toggle) && setToggle(_toggle); //to allow blocking
  }, [toggle]);

  // console.log("IFXToggleButton clone props", props);
  return React.cloneElement(children, {
    ...props,
    ..._toggleProps,
    onClick,
  });
};

export const asToggle = (children, toggleProps) => {
  // console.log("asToggle", children);
  return React.createElement(IFXToggleButton, {
    children,
    ...toggleProps,
  });
};

export const IFXIconButton = forwardRef((props, ref) => {
  const { type = "default" } = props;
  const selectedIconConfig = ButtonIconConfig[type.toLowerCase()];
  const selectedConfig = ButtonConfig[type.toLowerCase()];
  let newProps = {
    ...ButtonConfig.default,
    ...ButtonIconConfig.default,
    ...selectedConfig,
    mode: ButtonMode.ICON_MINI,
    ...selectedIconConfig,
    ...props,
  };

  //  console.log("IFXIconButton newProps", newProps);
  return <IFXButton ref={ref} tooltip {...newProps} />;
});

export const IFXPrimaryIconButton = props => {
  const DEFAULT_PROPS = {
    color: ColorOptions.ifxBrand,
  };

  return <IFXIconButton {...DEFAULT_PROPS} {...props} />;
};

export const IFXGridPrimaryIconButton = props => {
  const DEFAULT_PROPS = {
    className: "m-0 ml-2",
    color: ColorOptions.ifxBrand,
    size: "md",
  };

  return <IFXIconButton {...DEFAULT_PROPS} {...props} />;
};

export const IFXGridSecondaryIconButton = forwardRef((props, ref) => {
  const DEFAULT_PROPS = {
    className: "m-0 ml-2",
    color: ColorOptions.ifxAlternate,
  };

  return <IFXIconButton ref={ref} {...DEFAULT_PROPS} {...props} />;
});

export const IFXGridRowActionIconButton = forwardRef((props, ref) => {
  const DEFAULT_PROPS = {
    className: "mx-1",
    color: ColorOptions.ifxBrand,
  };

  return <IFXIconButton ref={ref} {...DEFAULT_PROPS} {...props} />;
});

export const IFXPageHeaderIconButton = props => {
  const DEFAULT_PROPS = {
    className: "m-0 ml-2",
    color: ColorOptions.ifxLight,
    size: "lg",
  };

  return <IFXIconButton {...DEFAULT_PROPS} {...props} />;
};

export const IFXAccordionHeaderIconButton = props => {
  const DEFAULT_PROPS = {
    className: "ml-2 my-0",
    color: ColorOptions.white,
    size: "md",
  };

  return <IFXIconButton {...DEFAULT_PROPS} {...props} />;
};

IFXButton.propTypes = {
  className: PropTypes.string,
  type: PropTypes.oneOf([
    ...Object.keys(ButtonConfig),
    ...Object.keys(ButtonIconConfig),
  ]),
  mode: PropTypes.oneOf(Object.values(ButtonMode)),
  outline: PropTypes.bool,
  backgroundStyle: PropTypes.oneOf([false, "btn-transparent"]),
  hoverStyle: PropTypes.oneOf(["", "btn-transition", "btn-hover-shine"]),
  wider: PropTypes.bool,
  size: PropTypes.string,
  color: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  htmlType: PropTypes.oneOf(["submit", "reset", "button"]),
  shape: PropTypes.oneOf(["square", "circle"]),
  iconProps: PropTypes.object,
  buttonConfig: PropTypes.oneOf([false, PropTypes.object]),
  shadow: PropTypes.bool,
  bubbleTip: PropTypes.string,
};
